import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  removeNotification,
  showNotification,
  updateNotification,
} from 'src/redux/notification/notification.slice';
import type { IMessage, Theme } from 'src/redux/notification/notification.types';
import type { BillingJobStatusResponse, BillingJobStatusValue } from 'src/redux/billing/billing.types';
import { invisiblyApi } from 'src/redux/api/invisibly-api';

const POLL_INTERVAL_MS = 4000;
const COMPLETE_DISMISS_MS = 4000;
const TOAST_TITLE = 'Billing generation';

function notificationIdForJob(billingMonthId: string): string {
  return `billing-generation-${billingMonthId}`;
}

function messageForStatus(status: BillingJobStatusValue, errorMessage?: string | null): {
  summary: string;
  theme: Theme;
  sticky: boolean;
  life?: number;
} {
  switch (status) {
    case 'pending':
      return { summary: 'Starting billing generation…', theme: 'neutral', sticky: true };
    case 'fetching_data':
      return { summary: 'Fetching data…', theme: 'neutral', sticky: true };
    case 'generating_invoices':
      return { summary: 'Generating invoices…', theme: 'neutral', sticky: true };
    case 'complete':
      return {
        summary: 'Billing generation complete.',
        theme: 'success',
        sticky: false,
        life: COMPLETE_DISMISS_MS,
      };
    case 'failed':
      return {
        summary: errorMessage
          ? `Billing generation failed: ${errorMessage}`
          : 'Billing generation failed.',
        theme: 'warning',
        sticky: true,
      };
    default:
      return { summary: 'Billing generation in progress…', theme: 'neutral', sticky: true };
  }
}

function isTerminalStatus(status: BillingJobStatusValue): boolean {
  return status === 'complete' || status === 'failed';
}

export function useBillingGenerationToast() {
  const dispatch = useDispatch();
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activeJobIdRef = useRef<string | null>(null);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    activeJobIdRef.current = null;
  }, []);

  const pushToast = useCallback(
    (billingMonthId: string, status: BillingJobStatusValue, errorMessage?: string | null) => {
      const toastId = notificationIdForJob(billingMonthId);
      const { summary, theme, sticky, life } = messageForStatus(status, errorMessage);
      const payload: IMessage = {
        id: toastId,
        title: TOAST_TITLE,
        summary,
        theme,
        sticky,
        ...(life != null ? { life } : {}),
      };
      dispatch(updateNotification(payload));
    },
    [dispatch]
  );

  const applyStatusUpdate = useCallback(
    (payload: BillingJobStatusResponse) => {
      const billingMonthId = payload.billing_month_id;
      pushToast(billingMonthId, payload.status, payload.error_message);

      if (isTerminalStatus(payload.status)) {
        stopPolling();
      }
    },
    [pushToast, stopPolling]
  );

  const pollOnce = useCallback(
    async(billingMonthId: string) => {
      try {
        const result = await dispatch(
          invisiblyApi.endpoints.getBillingJobStatus.initiate(billingMonthId, {
            forceRefetch: true,
          })
        ).unwrap();

        applyStatusUpdate(result);
      } catch {
        dispatch(
          updateNotification({
            id: notificationIdForJob(billingMonthId),
            title: TOAST_TITLE,
            summary: 'Unable to refresh billing status. The job is still running.',
            theme: 'info',
            sticky: true,
          })
        );
      }
    },
    [applyStatusUpdate, dispatch]
  );

  const showInitialToast = useCallback(
    (billingMonthId: string) => {
      dispatch(
        showNotification({
          id: notificationIdForJob(billingMonthId),
          title: TOAST_TITLE,
          summary: 'Starting billing generation…',
          theme: 'neutral',
          sticky: true,
        })
      );
    },
    [dispatch]
  );

  const startTracking = useCallback(
    (billingMonthId: string) => {
      stopPolling();
      activeJobIdRef.current = billingMonthId;
      showInitialToast(billingMonthId);

      void pollOnce(billingMonthId);

      pollTimerRef.current = setInterval(() => {
        if (activeJobIdRef.current) {
          void pollOnce(activeJobIdRef.current);
        }
      }, POLL_INTERVAL_MS);
    },
    [pollOnce, showInitialToast, stopPolling]
  );

  const dismissToast = useCallback(
    (billingMonthId: string) => {
      dispatch(
        removeNotification({
          id: notificationIdForJob(billingMonthId),
          summary: '',
          theme: 'neutral',
        })
      );
    },
    [dispatch]
  );

  useEffect(() => () => stopPolling(), [stopPolling]);

  return {
    startTracking,
    stopPolling,
    dismissToast,
  };
}
